import React, { useContext, useState, useRef } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { Switch } from 'antd';
import { useQuery } from 'react-query';
import { getAxiosTokenInstance } from '../../utils/axiosInstance';
import { NecttosContext } from '../../context/NecttosContext';
import { Table, Tbody, Td, Th, Thead, Tr, useSortableData } from '../../NecttosComp/Table/Table';
import { Body, Header, Main } from '../../NecttosComp/Layout/Layout';
import Button from '../../NecttosComp/Button/Button';
import Input2 from '../../NecttosComp/Input/Input2';
import { OLoading } from '../../static/svg/icons';
import Instruction from '../../Documentation/Instruction';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

const getFormsSettings =
	({ year, collegeId }) =>
	async () => {
		const instance = await getAxiosTokenInstance();
		try {
			const resp = await instance.get('/college/forms/getDefaultSettings', {
				params: {
					year,
					collegeId,
				},
			});
			if (resp.data.statusCode === 400) {
				throw new Error(resp.data.message || 'API Error');
			} else {
				return { question: resp?.data?.settings, classes: resp?.data?.courses };
			}
		} catch (error) {
			throw new Error(error?.response?.data?.message || error.message || 'API Error');
		}
	};

const ckeckNameAvailable = async (name, collegeId) => {
	const instance = await getAxiosTokenInstance();
	try {
		const res = await instance.get('/college/forms/checkId', {
			params: {
				formId: name,
				collegeId: collegeId,
			},
		});
		return res.data;
	} catch (error) {
		toast.error('Error checking name availability');
	}
};

const getAllForms = async ({ collegeId }) => {
	const instance = await getAxiosTokenInstance();
	try {
		const res = await instance.get('/college/forms/getAllForms', {
			params: {
				collegeId: collegeId,
			},
		});

		if (res.status === 200) {
			return res.data;
		} else {
			toast.error('Error fetching forms');
		}
	} catch (error) {
		console.log(error);

		toast.error('Error fetching forms');
	}
};

function FormsManagement({ onClose }) {
	const randomLetter = () => String.fromCharCode(65 + Math.floor(Math.random() * 26));
	const { collegeId, college, profile } = useContext(NecttosContext);
	const [confirmVisible, setConfirmVisible] = useState(false);
	const [search, setSearch] = useState('');
	const [form, setForm] = useState({
		title: '',
		description: '',
		formId: (college?.collegeCode ? `${college?.collegeCode}-` : '') + randomLetter() + Math.floor(1000 + Math.random() * 9000),
		acceptResponse: false,
		styles: {},
		formData: [],
		paymentNeed: false,
		amount: '',
	});
	const [modal, setModal] = useState('');
	const [loading, setLoading] = useState(false);
	const [response, setResponse] = useState([]);
	const [filter, setFilter] = useState({
		year: '',
	});
	const [editingIndex, setEditingIndex] = useState(null);
	const [edited, setEdited] = useState('');
	const [editedOptions, setEditedOptions] = useState([]);
	const componentRef = useRef();

	const startEditing = (index) => {
		setEditingIndex(index);
		setEdited(form.formData[index]);
		setEditedOptions(form.formData[index].options || []);
	};
	const saveEditing = (index) => {
		const updatedFormData = [...form.formData];
		updatedFormData[index] = {
			...updatedFormData[index],
			name: edited.name,
			options: ['dropdown', 'mdrop', 'multi-select', 'mdrop'].includes(edited?.type) ? editedOptions : [],
			type: edited.type,
		};
		setForm((prev) => ({ ...prev, formData: updatedFormData }));
		setEditingIndex(null);
	};

	const { data: nameAvail, isLoading: nameLoading } = useQuery(['checkNameAvailable', form.formId], () => ckeckNameAvailable(form.formId, collegeId), {
		enabled: !!form.formId && modal == 'newForm',
	});
	// let { data: drops = [] } = useQuery(['getDataBank'], getSettings(collegeId, 'dataBank', true, 'student', { type: 'Active Status', status: 'Current Active' }), { enabled: !!collegeId, refetchOnMount: false, refetchOnWindowFocus: false, refetchInterval: false, cacheTime: 60 * 60 * 1000, staleTime: 60 * 60 * 1000 });

	const { data: dataBank = {}, isFetching: isQuestionFetching } = useQuery(['getDataBank', filter?.year], getFormsSettings({ year: filter?.year, collegeId }), {
		enabled: !!collegeId,
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		refetchInterval: false,
		cacheTime: 60 * 60 * 1000,
		staleTime: 60 * 60 * 1000,
	});
	const years = [new Date().getFullYear(), new Date().getFullYear() + 1, new Date().getFullYear() - 1];
	const drops = dataBank?.question || [];
	const classes = dataBank?.classes || [];

	const {
		data,
		isLoading: isFetching,
		refetch,
	} = useQuery(['getAllForms'], () => getAllForms({ collegeId }), {
		enabled: !!collegeId,
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		refetchInterval: false,
	});
	const forms = data?.data || [];
	const paymentEnabled = data?.paymentEnabled || false;

	const newForm = async () => {
		if (!form.title || !form.description || !form.formId) {
			toast.error('Please fill all the fields');
			return;
		}
		if (nameLoading) {
			toast.error('Checking name availability...');
			return;
		}
		if (nameAvail?.available === false) {
			toast.error('Name is not available');
			return;
		}
		setLoading(true);
		const instance = await getAxiosTokenInstance();
		try {
			const res = await instance.post('/college/forms/createForm', {
				title: form.title,
				description: form.description,
				formId: form.formId,
				collegeId: collegeId,
			});
			if (res.status === 200) {
				toast.success('Form created successfully');
				setLoading(false);
				setForm({
					...form,
					_id: res.data._id,
				});
				setModal('editForm');
			} else {
				toast.error('Error creating form');
				setLoading(false);
			}
		} catch (error) {
			toast.error('Error creating form');
			setLoading(false);
		}
	};

	const handleSave = async ({ deleteNeeded, formData }) => {
		setLoading(true);

		if (deleteNeeded) {
			const instance = await getAxiosTokenInstance();
			try {
				const res = await instance.post('/college/forms/postForm', {
					formId: form.formId,
					collegeId: collegeId,
					_id: form?._id,
					isDelete: true,
				});
				if (res.status === 200) {
					toast.success('Form deleted successfully');
					setModal('');
					refetch();
					handleClose();
					setConfirmVisible(false);
				} else {
					toast.error('Error deleting form');
				}
			} catch (error) {
				console.log(error);

				toast.error('Error deleting form');
			} finally {
				setLoading(false);
			}
			return;
		} else {
			if (form?.formData?.length === 0) {
				toast.error('Please add at least one question');
				setLoading(false);
				return;
			}
			if (!form?.formData?.some((item) => item._id === 'phoneNumber' || item._id === 'email')) {
				toast.error('Please add either phone number or email');
				setLoading(false);
				return;
			}

			if (!form?.formData?.some((item) => item._id === 'name')) {
				toast.error('Please add name field');
				setLoading(false);
				return;
			}

			try {
				const instance = await getAxiosTokenInstance();
				const res = await instance.post('/college/forms/postForm', {
					_id: form._id,
					formId: form.formId,
					title: form.title,
					description: form.description,
					acceptResponse: form.acceptResponse,
					formData: form.formData,
					styles: form.styles,
					collegeId: collegeId,
					paymentNeed: form.paymentNeed,
					amount: form.amount,
				});
				if (res.status === 200) {
					toast.success('Form updated successfully');
					setModal('');
					refetch();
					handleClose();
				} else {
					toast.error('Error updating form');
				}
			} catch (error) {
				toast.error('Error saving form');
			} finally {
				setLoading(false);
			}
		}
	};

	const handleOpenEditor = async (formData) => {
		setLoading(formData.formId);
		const instance = await getAxiosTokenInstance();
		try {
			const res = await instance.get('/college/forms/getFormDetails', {
				params: {
					formId: formData.formId,
					collegeId: collegeId,
				},
			});
			if (res.status === 200) {
				setForm(res.data.data);
				refetch();
				setModal('editForm');
			} else {
				toast.error('Error fetching form');
			}
		} catch (error) {
			toast.error('Error fetching form');
		} finally {
			setLoading(false);
		}
	};
	const openResponse = async () => {
		setLoading(true);
		const instance = await getAxiosTokenInstance();
		try {
			const res = await instance.get('/college/forms/getFormResponses', {
				params: {
					formId: form.formId,
					collegeId: collegeId,
				},
			});
			if (res.status === 200) {
				setForm((prev) => ({
					...prev,
					responseCount: res.data.list?.length,
				}));
				setResponse(res.data.list);
				setModal('viewResponse');
			} else {
				toast.error('Error fetching form responses');
			}
		} catch (error) {
			toast.error('Error fetching form responses');
		} finally {
			setLoading(false);
		}
	};

	const handleOptionChange = (value, i) => {
		const newOptions = [...editedOptions];
		newOptions[i] = value;
		setEditedOptions(newOptions);
	};

	const addOption = () => {
		setEditedOptions((prev) => [...prev, '']);
	};

	const removeOption = (i) => {
		setEditedOptions((prev) => prev.filter((_, idx) => idx !== i));
	};

	const handleClose = () => {
		setModal('');
		setForm({
			title: '',
			description: '',
			formId: (college?.collegeCode ? `${college?.collegeCode}-` : '') + randomLetter() + Math.floor(1000 + Math.random() * 9000),
			acceptResponse: false,
			styles: {},
			formData: [],
			paymentNeed: false,
			amount: '',
		});
		setSearch('');
	};

	const renderModal = () => {
		switch (modal) {
			case 'newForm':
				return (
					<Main title='New Form' width='100%' height='100%' zIndex='10'>
						<div className='flex flex-col items-center justify-center w-full h-full gap-8'>
							<div className='w-full max-w-lg bg-slate-50 dark:bg-[#18181b] rounded-xl shadow-lg p-8 flex flex-col items-center gap-6'>
								<div className='flex flex-col '>
									<div className='flex items-center justify-center gap-2 w-[60vw]'>
										<span className='dark:text-white'>
											Your Link :<span className='font-mono text-blue-600 dark:text-blue-400 break-all'>https://necttos.com/forms/{form?.formId}</span>
										</span>
										<button
											className='ml-2 px-2 py-1 bg-gray-200 dark:text-white dark:bg-gray-700 rounded text-xs hover:bg-gray-300 dark:hover:bg-gray-600'
											onClick={() => {
												navigator.clipboard.writeText(`https://necttos.com/forms/${form?.formId}`);
												toast.success('Link copied!');
											}}
											type='button'>
											Copy
										</button>
									</div>
								</div>
								<p className='text-gray-400 text-[8px]'>like [COLLEGE_SHORT_NAME]-[RANDOM_THREE_LETTER]-[RANDOM_THREE_LETTER]</p>
								<Input2 type='text' fieldName='Form Name' state={form.formId} setState={(e) => setForm({ ...form, formId: e?.replace(' ', '-') })} className='w-full border-0' />
								{!nameLoading && nameAvail?.available === false && <span className='text-red-500 text-xs'>Name is not available</span>}
								{!nameLoading && nameAvail?.available === true && <span className='text-green-500 text-xs'>Name is available</span>}
								{nameLoading && <span className='text-gray-500 text-xs'>Checking name availability...</span>}
								<Input2 type='text' fieldName='Title' state={form.title} setState={(e) => setForm({ ...form, title: e })} className='w-full h-10 border-0' />
								<Input2 type='textarea' fieldName='Description' state={form.description} setState={(e) => setForm({ ...form, description: e })} className='w-full min-h-[80px] border-0' />
								<div className='flex w-full justify-evenly gap-4 mt-4'>
									<Button type='close' onClick={handleClose} className='w-1/2 bg-gray-300 text-gray-800 hover:bg-gray-400'>
										Close
									</Button>
									<Button type='save' onClick={newForm} className='w-1/2 bg-green-600 text-white hover:bg-green-700'>
										Create
									</Button>
								</div>
							</div>
						</div>
					</Main>
				);
			case 'viewResponse':
				return (
					<Main title='View Responses' zIndex='10' width={'100%'} height={'100%'}>
						<Header>
							<div className='relative flex items-center space-x-2 bg-slate-300 p-2 border border-gray-300 rounded' onClick={openResponse}>
								<Switch checked={form?.tableView} onChange={(e) => setForm((prev) => ({ ...prev, tableView: e }))} />
								<label className='text-sm font-medium text-gray-700'>Switch to Table View</label>
							</div>
							<div className='flex justify-end space-x-2'>
								<Button type='print' tableRef={componentRef.current}>
									Print
								</Button>
								<Button type='excel' tableRef={componentRef.current}>
									Excel
								</Button>
								<Button type='close' onClick={handleClose} className='text-red-500'>
									Close
								</Button>
							</div>
						</Header>
						<Body>
							<div ref={componentRef} className='printable flex flex-col gap-4 w-full h-full'>
								{form?.responseCount > 0 ? (
									form?.tableView ? (
										<Table width={`${300 * form?.formData.length + 20}px`}>
											<Thead>
												<Tr>
													<Th width='20px'>S.No</Th>
													{form?.formData?.map((item, index) => (
														<Th key={index} width={'300px'}>
															{item.name}
														</Th>
													))}
												</Tr>
											</Thead>
											<Tbody>
												{response?.map((item, index) => (
													<Tr key={index} onClick={() => window.open(`https://www.necttos.com/response/${item._id}`, '_blank')}>
														<Td width='20px'>{index + 1}</Td>
														{form?.formData?.map((data, i) => (
															<Td key={i} width={'300px'}>
																{item[data._id]}
															</Td>
														))}
													</Tr>
												))}
											</Tbody>
										</Table>
									) : (
										<div className='bg-slate-50 dark:bg-[#23232a] rounded-xl shadow p-6 flex flex-col gap-4 h-[90vh]'>
											{response?.map((item, index) => (
												<div key={index} className='bg-gray-50 dark:bg-[#23232a] rounded p-2 shadow-sm flex items-center gap-2 w-full card flex-row'>
													<span className='text-gray-400 pr-2 h-full'>{index + 1}.</span>
													<div className='flex flex-col gap-2 w-full'>
														{form?.formData?.map((data, i) => (
															<div key={i} className='flex items-center justify-between'>
																<span className='font-medium dark:text-gray-200'>{data.name}</span>
																<span className='text-gray-500 dark:text-gray-400'>{item[data._id]}</span>
															</div>
														))}
													</div>
												</div>
											))}
										</div>
									)
								) : (
									<div className='bg-slate-50 dark:bg-[#23232a] rounded-xl shadow p-6 flex flex-col gap-4 h-[90vh] overflow-y-scroll'>
										<span className='text-gray-400 text-sm'>No responses found.</span>
									</div>
								)}
							</div>
						</Body>
					</Main>
				);

			case 'editForm':
				return (
					<Main title='Edit Form' zIndex='10' width={'100%'} height={'100%'}>
						<Body>
							<div className='grid grid-cols-1 md:grid-cols-2 gap-8 w-full'>
								<div className='bg-white dark:bg-[#1f1f27] rounded-2xl shadow-lg p-6 flex flex-col gap-6 h-[90vh] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-transparent'>
									<div className='flex flex-col gap-1 border-b border-gray-200 dark:border-gray-700 pb-6 mb-6 relative minh-[40vh'>
										<div className='flex flex-col sm:flex-row sm:items-center gap-6'>
											<div className='flex items-center justify-center w-20 h-20 bg-gray-200 dark:bg-gray-700 rounded-xl shadow'>
												<img src={college?.logo} alt='Logo' className='w-16 h-16 object-contain rounded-md' />
											</div>

											<div className='flex-1'>
												<h1 className='text-2xl font-bold text-gray-800 dark:text-white'>{college?.collegeName}</h1>
												<p className='text-sm text-gray-600 dark:text-gray-400 mt-1'>{college?.address}</p>
												<p className='text-sm text-gray-600 dark:text-gray-400'>{college?.accreditation}</p>
												{college?.website && (
													<a href={college?.website} target='_blank' rel='noopener noreferrer' className='inline-block text-sm text-blue-600 dark:text-blue-400 mt-1 hover:underline'>
														🌐 {college?.website}
													</a>
												)}
											</div>
										</div>

										<div className='my-2'>
											<Input2 type='text' fieldName='Form Title' state={form.title} setState={(e) => setForm({ ...form, title: e })} className=' w-full' />
											<Input2 type='textarea' fieldName='Form Description' state={form.description} setState={(e) => setForm({ ...form, description: e })} className=' w-full relative' />
										</div>
									</div>

									<div className='relative'>
										<DragDropContext
											onDragEnd={(result) => {
												if (!result.destination) return;
												const reordered = Array.from(form.formData);
												const [removed] = reordered.splice(result.source.index, 1);
												reordered.splice(result.destination.index, 0, removed);
												setForm((prev) => ({ ...prev, formData: reordered }));
											}}>
											<Droppable droppableId='form-questions'>
												{(provided) => (
													<div className='flex flex-col gap-4' {...provided.droppableProps} ref={provided.innerRef}>
														{form?.formData?.map((item, index) => (
															<Draggable key={item._id || index} draggableId={item._id?.toString() || `${index}`} index={index}>
																{(provided, snapshot) => (
																	<div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps} className={`bg-slate-100 dark:bg-[#2a2a35] rounded-xl px-4 py-2 shadow-sm flex flex-col gap-2 w-full ${snapshot.isDragging ? 'ring-2 ring-blue-400' : ''}`}>
																		<div className='flex items-center gap-2'>
																			<span className='cursor-move text-gray-400 pr-2'>☰</span>

																			{editingIndex === index ? (
																				<input className='flex-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded px-2 py-1' value={edited.name} onChange={(e) => setEdited((prev) => ({ ...prev, name: e.target.value }))} autoFocus />
																			) : (
																				<span className='flex-1 font-medium dark:text-gray-200 cursor-pointer' onClick={() => startEditing(index)}>
																					{item.name}
																				</span>
																			)}

																			{editingIndex === index ? (
																				<button className='text-blue-600 hover:underline text-sm' onClick={() => saveEditing(index)}>
																					Save
																				</button>
																			) : (
																				<button className='text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-sm' onClick={() => startEditing(index)} title='Edit question'>
																					✎
																				</button>
																			)}
																		</div>

																		{/* Options Editing */}
																		{editingIndex === index && ['dropdown', 'mdrop', 'multi-select', 'mdrop'].includes(edited?.type) ? (
																			<div className='flex flex-col gap-1'>
																				{editedOptions.map((opt, i) => (
																					<div key={i} className='flex items-center gap-2'>
																						<input type='text' className='flex-1 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100' value={opt} onChange={(e) => handleOptionChange(e.target.value, i)} />
																						<button onClick={() => removeOption(i)} className='text-red-600 hover:text-red-800' title='Remove option' type='button'>
																							×
																						</button>
																					</div>
																				))}
																				<button onClick={addOption} className='text-blue-600 hover:underline text-sm mt-1 self-start' type='button'>
																					+ Add option
																				</button>
																			</div>
																		) : (
																			<div className='flex items-center gap-2 mt-1 flex-wrap'>
																				{item?.options?.slice(0, 4)?.map((option, i) => (
																					<span key={i} className='text-sm text-gray-600 dark:text-gray-400 bg-gray-200 dark:bg-gray-700 rounded px-2 py-0.5'>
																						{option}
																					</span>
																				))}
																				{item?.options?.length >= 4 && <span className='text-sm text-gray-600 dark:text-gray-400 bg-gray-200 dark:bg-gray-700 rounded px-2 py-0.5'>...</span>}
																			</div>
																		)}
																		{item?.question && <span className='text-sm text-gray-600 dark:text-gray-400'>{item.question}</span>}
																		{editingIndex === index && edited?.type ? <Input2 type='drop' fieldName='Select Type' className='w-[7vw]' state={edited} setState={setEdited} returnKey='type' options={['dropdown', 'text', 'number', 'multi-select']} /> : <span className='text-sm text-gray-600 dark:text-gray-400'>{item?.type}</span>}
																	</div>
																)}
															</Draggable>
														))}
														{provided.placeholder}
													</div>
												)}
											</Droppable>
										</DragDropContext>
									</div>
								</div>

								<div className='bg-white dark:bg-[#1f1f27] rounded-2xl shadow-lg p-6 flex flex-col gap-1 h-[90vh] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-transparent'>
									<div className='flex items-center justify-end'>
										<div className='relative flex items-center space-x-2 bg-slate-300 p-2 border border-gray-300 rounded'>
											<div className='absolute -top-2 -left-2 flex items-center justify-center w-6 h-6 bg-red-600 text-white text-xs font-bold rounded-full'>{form?.responseCount || 0}</div>
											<Switch checked={form?.acceptResponse} onChange={(e) => setForm((prev) => ({ ...prev, acceptResponse: e }))} />
											<label className='text-sm font-medium text-gray-700'>Accept Response</label>
										</div>
										<Button type='fetch' onClick={openResponse} className='text-red-500'>
											Response
										</Button>
										<Button
											type='refresh'
											onClick={() => {
												navigator.clipboard.writeText(`https://necttos.com/forms/${form?.formId}`);
												toast.success('Link copied!');
											}}>
											Copy Link
										</Button>
										<Button type='save' onClick={handleSave}>
											Save
										</Button>
										<Button type='close' onClick={handleClose}>
											Close
										</Button>
									</div>

									<div className='flex justify-between items-center border-b pb-3 border-gray-200 dark:border-gray-700'>
										<div className='w-[60%]'>
											<h2 className='text-lg font-bold dark:text-white'>Question Bank</h2>
											<div className='flex flex-col sm:flex-row sm:items-center gap-2 w-[80%]  '>
												<div className='flex-1 '>
													<span className='block font-mono text-sm text-blue-600 dark:text-blue-400 '>https://necttos.com/forms/{form?.formId}</span>
												</div>
											</div>
										</div>

										{form?.paymentNeed ? (
											<div className='flex items-center gap-2'>
												<input type='text' value={form?.amount} onChange={(e) => setForm((prev) => ({ ...prev, amount: e.target.value }))} placeholder='Payment Amount' className='p-2 rounded border border-gray-300 dark:bg-[#18181b] dark:text-white dark:border-gray-600' />
												<Button type='close' onClick={() => setForm((prev) => ({ ...prev, paymentNeed: false }))}>
													Cancel
												</Button>
											</div>
										) : (
											<Button type='save' disabled={!paymentEnabled} onClick={() => setForm((prev) => ({ ...prev, paymentNeed: true }))}>
												+ Payment
											</Button>
										)}
										{!paymentEnabled && <p className='text-red-400'>The college Has No Payment Gateway enabled</p>}
									</div>
									<div className='flex'>
										<input type='text' value={search} onChange={(e) => setSearch(e.target.value)} placeholder='Search questions...' className='p-2 rounded border border-gray-300 dark:bg-[#18181b] dark:text-white dark:border-gray-600' />
										<Button type='save' onClick={() => setForm((prev) => ({ ...prev, formData: [{ _id: 'newId', name: 'New Field', type: 'text', question: 'custom' }, ...(prev.formData || [])] }))}>
											+ Add New Field
										</Button>
										{isQuestionFetching && (
											<div className='flex'>
												<OLoading styles={{ height: 10, width: 10 }} />
											</div>
										)}
									</div>

									<div className='flex flex-col gap-3 max-h-[60vh] overflow-y-auto px-4 py-3 bg-slate-50 dark:bg-[#1e1e2a] rounded-lg shadow-inner'>
										{[...(drops?.student || []), ...(form?.formData.filter((x) => x.question === 'custom') || []), { _id: 'courseName', name: 'Course Name', type: 'mdrop', question: 'Course Short Name' }, { _id: 'longCourseName', name: 'Course Name', type: 'mdrop', question: 'Course Long Name' }]
											?.filter((x) => (search ? x.name.toLowerCase().includes(search.toLowerCase()) : true))
											?.map((item, index) => {
												const checked = !!form.formData.find((x) => x._id === item._id);

												if (item._id === 'courseName' || item._id === 'longCourseName') {
													const optionList = item._id === 'courseName' ? classes.map((x) => x.shortName) : classes.map((x) => x.longName);
													return (
														<label key={item._id || index} className='flex items-center justify-between gap-3 p-3 rounded-lg bg-white dark:bg-[#2a2a35] hover:bg-slate-200 dark:hover:bg-[#18181b] transition-shadow shadow-sm cursor-pointer'>
															<div className='flex items-center gap-3 flex-1 min-w-0'>
																<input
																	type='checkbox'
																	checked={checked}
																	disabled={optionList.length == 0}
																	onChange={() =>
																		setForm((prev) => ({
																			...prev,
																			formData: checked || optionList.length == 0 ? prev.formData.filter((x) => x._id !== item._id) : [...prev.formData, { ...item, options: optionList }],
																		}))
																	}
																	className='accent-blue-600 w-5 h-5 flex-shrink-0'
																/>
																{isQuestionFetching && <OLoading styles={{ height: 10, width: 10 }} />}
																<span className='font-semibold dark:text-gray-200 truncate'>{item?.question || item.name}</span>
															</div>
															<Input2 type='drop' fieldName='Select Year' setState={setFilter} state={filter} options={years} className='w-[15vw] max-w-xs h-10 border border-gray-300 dark:border-[#3b3b49] bg-white dark:bg-[#1f1f27] rounded-lg shadow-sm' width='100%' returnKey='year' />
														</label>
													);
												}

												return (
													<label key={item._id || index} className='flex items-center gap-3 px-4 py-3 rounded-lg bg-white dark:bg-[#2a2a35] hover:bg-slate-200 dark:hover:bg-[#18181b] transition-shadow shadow-sm cursor-pointer'>
														<input
															type='checkbox'
															checked={checked}
															onChange={() =>
																setForm((prev) => ({
																	...prev,
																	formData: checked ? prev.formData.filter((x) => x._id !== item._id) : [...prev.formData, item],
																}))
															}
															className='accent-blue-600 w-5 h-5 flex-shrink-0'
														/>
														<div className='flex flex-col min-w-0'>
															<span className='font-semibold dark:text-gray-200 truncate'>{item.name}</span>
															<span className='text-xs text-gray-500 dark:text-gray-400 truncate select-none'>{`(${item._id})`}</span>
														</div>
													</label>
												);
											})}
										{(!drops?.student || drops?.student.length === 0) && <span className='text-gray-400 text-sm text-center py-4 select-none'>No questions found.</span>}
									</div>
								</div>
							</div>
						</Body>
					</Main>
				);
			default:
				return null;
		}
	};

	return (
		<Main title='Form Management' width={'100%'} height={'100%'}>
			<Toaster />
			{renderModal()}
			<Header>
				<div className='flex justify-between items-center w-full'>
					<div className='flex items-center'>
						<Input2 type={'text'} fieldName='Search' state={search} setState={setSearch} className='h-[40px] w-[40vw] sm:w-[30vw] md:w-[60vw] lg:w-[70vw] border-0' />
					</div>
					<div className='flex items-center'>
						<Button type='save' onClick={() => setModal('newForm')} className='text-red-500'>
							+ New Form
						</Button>
						<Instruction keyId={'formsManagement'} />
						<Button type='close' onClick={() => onClose()} className='text-red-500'>
							Close
						</Button>
					</div>
				</div>
			</Header>
			<Body className='w-full h-full p-4 overflow-y-auto'>
				{forms?.length > 0 ? (
					<div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 '>
						{forms
							?.filter((x) => x.title.toLowerCase().includes(search.toLowerCase()) || x.description.toLowerCase().includes(search.toLowerCase()) || x.formId.toLowerCase().includes(search.toLowerCase()))
							?.map((form) => (
								<div
									key={form._id}
									onClick={() => {
										setForm(form);
										handleOpenEditor(form);
									}}
									className='cursor-pointer bg-gray-50 dark:bg-neutral-900 rounded-3xl shadow-md hover:shadow-2xl transition-all duration-200 border border-gray-200 dark:border-neutral-700 p-6 flex flex-col gap-3'>
									{loading === form.formId ? (
										<div className='flex items-center justify-center h-32'>
											<OLoading styles={{ height: 24, width: 24 }} />
										</div>
									) : (
										<>
											<div className='flex justify-between items-center'>
												<div>
													<div className='text-xs text-gray-500 dark:text-gray-400'>Form ID</div>
													<div className='text-base font-semibold text-slate-800 dark:text-white'>{form.formId}</div>
												</div>
												<span className={`text-xs font-semibold px-3 py-1 rounded-full ${form.acceptResponse ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'}`}>{form.acceptResponse ? 'Accepting' : 'Not Accepting'}</span>
											</div>
											<div>
												<div className='text-xs text-gray-500 dark:text-gray-400'>Title</div>
												<div className='text-base font-medium text-slate-700 dark:text-gray-200 truncate'>{form.title}</div>
											</div>
											<div>
												<div className='text-xs text-gray-500 dark:text-gray-400'>Description</div>
												<div className='text-sm text-slate-600 dark:text-gray-300 line-clamp-4'>{form.description || 'No description available.'}</div>
											</div>
											<div className='mt-4 pt-3 border-t border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-3' onClick={(e) => e.stopPropagation()}>
												<div className='flex gap-4 text-sm'>
													<button onClick={() => navigator.clipboard.writeText(`https://necttos.com/forms/${form.formId}`)} className='text-blue-600 dark:text-blue-400 hover:underline'>
														📋 Copy
													</button>
													<button
														onClick={() =>
															navigator.share?.({
																title: form.title,
																text: form.description,
																url: `https://necttos.com/forms/${form.formId}`,
															})
														}
														className='text-green-600 dark:text-green-400 hover:underline'>
														🔗 Share
													</button>
													<a href={`mailto:?subject=${encodeURIComponent(form.title)}&body=${encodeURIComponent(`Form ID: ${form.formId}\nhttps://necttos.com/forms/${form.formId}`)}`} className='text-purple-600 dark:text-purple-400 hover:underline'>
														✉️ Mail
													</a>
												</div>
												<div className='flex gap-4 text-sm'>
													<button onClick={() => handleOpenEditor(form)} className='text-yellow-600 dark:text-yellow-400 hover:underline'>
														✏️ Edit
													</button>
													<button
														onClick={() => {
															setForm(form);
															setConfirmVisible(true);
														}}
														className='text-red-600 dark:text-red-400 hover:underline'>
														🗑️ Delete
													</button>
												</div>
											</div>

											{confirmVisible && (
												<Main title='Delete Form' width='50%' height='30%' onClick={() => setConfirmVisible(false)}>
													<div className='fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center'>
														<div className='bg-white dark:bg-neutral-800 w-[90%] max-w-md rounded-2xl shadow-xl p-6'>
															<h2 className='text-lg font-bold text-slate-800 dark:text-white mb-3'>Delete Form</h2>
															<p className='text-sm text-gray-600 dark:text-gray-300 mb-6'>Are you sure you want to delete this form? This action cannot be undone.</p>
															<div className='flex justify-end gap-3'>
																<button onClick={onClose} className='px-4 py-2 text-sm rounded-lg bg-gray-200 dark:bg-neutral-700 text-gray-800 dark:text-white hover:bg-gray-300 dark:hover:bg-neutral-600'>
																	Cancel
																</button>
																<button onClick={() => handleSave({ deleteNeeded: true })} className='px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700'>
																	Delete
																</button>
															</div>
														</div>
													</div>
												</Main>
											)}
										</>
									)}
								</div>
							))}
					</div>
				) : (
					<div className='flex items-center justify-center w-full h-full'>
						<span className='text-gray-500 dark:text-gray-400'>No forms found</span>
					</div>
				)}
			</Body>
		</Main>
	);
}

export default FormsManagement;
